import { getLastPosition } from "../../../lib/reportUtilities.js";
import { CCInteractiveTable } from "../../cclib/CCInteractiveTable";
import { CCSetup } from "../../cclib/CCSetup";
import { CCStructure } from "../../cclib/CCStructure";
import { afcsStructure } from "./structure";
import {
  genericTablePopulate,
  genericTableSetup,
  genericTableUpdate,
  presSummarySetup,
  presSummaryUpdate,
  reputationSetup,
  reputationUpdate,
  StanfordEM_Tables
} from "./tables";

import { TimeObject } from "@easycv/common/types/util";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat.js";
import { hasTimeErr, monthErr } from "../../../lib/cvDataUtilities.js";
import { CCIdentifiableError } from "../../cclib/types/setup.js";
import { getReputationData } from "../shared/util";
import { DISABLE_EMAILS } from "../stanford_em";
dayjs.extend(LocalizedFormat);

export const afcsDataPrefix = "stanford_em_afcs_";
export const SECTIONS_WITH_COUNTS = [
  "custom-courses",
  "custom-grants",
  "custom-peer-original",
  "custom-peer-other",
  "custom-peer-articles",
  "custom-peer-articles",
  "custom-abstracts",
  "custom-chapters",
  "custom-books",
  "custom-book-reviews",
  "custom-digital-pub",
  "custom-committee-not",
  "custom-committee-local",
  "custom-committee-regional",
  "custom-committee-national",
  "custom-committee-international",
  "custom-committee-uni"
];

function shouldReputationBeSkipped(cc: CCSetup) {
  const SKIP_REP_IF = ["Professor", "Clinical Professor"];

  // if the cc is filled out already, we want that title instead of the lastPosition.
  const data = cc.newDef?.career?.[cc.viewYear]?.data;
  const rank = data?.[afcsDataPrefix + "faculty_rank"];

  if (rank != undefined && typeof rank === "string" && rank.trim().length) {
    return SKIP_REP_IF.includes(rank);
  } else {
    const positions = getLastPosition(cc.newDef, { returnAll: true }) ?? [];
    const lastPosition = positions[positions.length - 1] ?? {};
    lastPosition.title ??= "";
    return SKIP_REP_IF.includes(lastPosition.title);
  }
}
function hideReputationHtml(cc: CCSetup) {
  const reputationHtml = $('[data-reputation-mark="start"]')
    .nextUntil($('[data-reputation-mark="end"]'))
    .addBack();
  reputationHtml.attr("data-reputation-hidden", "");
  reputationHtml.hide();
}
function showReputationHtml(cc: CCSetup) {
  const reputationHtml = $('[data-reputation-mark="start"]')
    .nextUntil($('[data-reputation-mark="end"]'))
    .addBack();
  reputationHtml.removeAttr("data-reputation-hidden");
  reputationHtml.show();
}
export function updateAFCS(cc: CCSetup) {
  if (!cc.checkPermissions().writableAsMenteeIdentity()) {
    $(".syncSection").attr("disabled", "disabled");
  } else {
    $(".syncSection").removeAttr("disabled");
  }
  const iiiHeader = $('[data-key="gs_academic_year_replace"]');
  cc.removeHTMLElement(iiiHeader.children(":not(div)"));
  iiiHeader.prepend(
    `<span>III.\tGrants Submitted (submissions in the academic year ${cc.getPeriod()})</span>`
  );

  if (shouldReputationBeSkipped(cc)) {
    hideReputationHtml(cc);
  } else {
    showReputationHtml(cc);
  }
}

export function outputAFCS(cc: CCSetup) {
  const dataOverride: Record<string, unknown> = {
    cv_owner_name: cc.newDef.bio["name"],
    academic_year: cc.period
      .split("-")
      .map((v) => dayjs(v.trim()).format("LL"))
      .join(" - ")
  };

  const data = cc.newDef.career[cc.viewYear].data ?? {};
  for (const sectionWithCount of SECTIONS_WITH_COUNTS) {
    const sectionData = Object.values(
      data[afcsDataPrefix + sectionWithCount] ?? {}
    );
    const count = sectionData.reduce(
      (acc: number, curr: unknown) => (curr != undefined ? acc + 1 : acc),
      0
    );
    dataOverride[`${sectionWithCount}-count`] = count;
  }

  const holdDataPrefix = cc.dataPrefix;
  cc.dataPrefix = afcsDataPrefix;
  cc.conferenceExport(
    "stanford_em_afcs.docx",
    "Stanford EM AFCS",
    {
      ...dataOverride,
      ...getReputationData(data, afcsDataPrefix)
    },
    () => "",
    undefined,
    () => void (cc.dataPrefix = holdDataPrefix)
  );
}

const grantTableError = (key: string, cc: CCSetup): CCIdentifiableError[] => {
  const data = cc.customSectionData(key) as CCInteractiveTable;
  const errors: CCIdentifiableError[] = [];
  for (let i = 0; i < data.rows.length; ++i) {
    if (
      ["Choose", "", undefined].includes(data.rows[i]?.["awarded"] as string)
    ) {
      errors.push({
        isError: true,
        uniqueJquerySelector: `[data-key="${key}"] tbody #${key}-${i} >:nth-child(8)`,
        tooltip: "Required"
      });
    }
  }
  return errors;
};

/**
  Checks an entry and returns null if the date is valid or a string explaining the issue if not.
  Treats end date as optional
*/
function endDateOptionalErrorCheck(entry: { time?: TimeObject }) {
  return (
    hasTimeErr(entry?.time, entry) ??
    monthErr(entry?.time?.start, entry) ??
    (entry?.time?.end === "ongoing" || entry?.time?.end == undefined
      ? null
      : monthErr(entry?.time?.end, entry))
  );
}
const courseTableError = (key: string, cc: CCSetup): CCIdentifiableError[] => {
  const data = cc.customSectionData(key) as CCInteractiveTable;
  const errors: CCIdentifiableError[] = [];
  const cvData = cc.newDef?.lists["teaching"];
  for (let i = 0; i < data.rows.length; ++i) {
    const entry = cvData[data.rows[i].id];
    const err = endDateOptionalErrorCheck(entry);
    if (err !== null) {
      errors.push({
        isError: true,
        uniqueJquerySelector: `[data-key='${key}'] tbody #${key}-${i} >:first-child`,
        tooltip: err
      });
    }
    if (["Choose", "", undefined].includes(data.rows[i]["role"] as string)) {
      errors.push({
        isError: true,
        uniqueJquerySelector: `[data-key='${key}'] tbody #${key}-${i} >:nth-child(5)`,
        tooltip: "Required"
      });
    }
  }
  return errors;
};
const committeeTableError = (
  key: string,
  cc: CCSetup
): CCIdentifiableError[] => {
  const data = cc.customSectionData(key) as CCInteractiveTable;
  const errors: CCIdentifiableError[] = [];
  const cvData = cc.newDef?.lists["committeeService"];
  for (let i = 0; i < data.rows.length; ++i) {
    const entry = cvData[data.rows[i].id];
    const err = endDateOptionalErrorCheck(entry);
    if (err !== null) {
      errors.push({
        isError: true,
        uniqueJquerySelector: `[data-key="${key}"] tbody #${key}-${i} >:first-child`,
        tooltip: err
      });
    }
  }
  return errors;
};
const reputationTableError = (
  key: string,
  cc: CCSetup
): CCIdentifiableError[] => {
  if ($("[data-reputation-hidden]").length) {
    return [];
  }
  const data = cc.customSectionData(key) as CCInteractiveTable;
  const errors = [];
  for (let i = 0; i < data.rows.length; ++i) {
    const rowData = [
      data.rows[i]?.["first_name"],
      data.rows[i]?.["last_name"],
      data.rows[i]?.["email"],
      data.rows[i]?.["institution"]
    ];
    for (let k = 0; k < rowData.length; ++k) {
      if (!Boolean(rowData[k])) {
        errors.push({
          isError: true,
          uniqueJquerySelector: `[data-key="${key}"] tbody #${key}-${i} >:nth-child(${k + 2})`
        });
      }
    }
  }
  return errors;
};
export function setupAFCS(cc: CCSetup) {
  const structure = new CCStructure(afcsStructure);

  cc.generateFieldsFromStructure(structure, cc.ccRoot, false);

  for (const tableKey of Object.keys(StanfordEM_Tables)) {
    let errorCall = () => undefined;
    if (tableKey === "custom-courses") {
      errorCall = () => courseTableError(tableKey, cc);
    } else if (tableKey === "custom-grants") {
      errorCall = () => grantTableError(tableKey, cc);
    } else if (tableKey.includes("custom-committee-")) {
      errorCall = () => committeeTableError(tableKey, cc);
    }
    cc.defineCustomSection(
      tableKey,
      genericTableSetup,
      genericTableUpdate,
      errorCall
    );
  }
  cc.addQuietFields("custom-pres-summary");
  cc.defineCustomSection(
    "custom-pres-summary",
    presSummarySetup,
    presSummaryUpdate
  );
  cc.defineCustomSection(
    "custom-reputation",
    reputationSetup,
    reputationUpdate,
    () => reputationTableError("custom-reputation", cc)
  );
  cc.addButton(
    "primary",
    `<span>Sync section with CV</span>`,
    {
      relative: "after",
      selector: `[data-key="publications_header_sync_binding"]`
    },
    "syncSection",
    () => {
      if (!cc.checkPermissions().writableAsMenteeIdentity()) {
        return;
      }
      const allTables = cc.allCustomSectionData() as CCInteractiveTable[];
      const pubTables = allTables.filter((t) =>
        t.columns.some(({ db }) => db === "line")
      );
      for (const pubTable of pubTables) {
        genericTablePopulate(pubTable as CCInteractiveTable<any>, cc);
      }
      cc.update(cc.struct.structure);
    }
  );

  cc.addButton(
    "primary",
    `<span>Sync section with CV</span>`,
    {
      relative: "after",
      selector: `[data-key="presentations_header_sync_binding"]`
    },
    "syncSection",
    () => {
      if (!cc.checkPermissions().writableAsMenteeIdentity()) {
        return;
      }
      const allTables = cc.allCustomSectionData() as CCInteractiveTable[];
      const presTables = allTables.filter((t) =>
        t.columns.some(({ db }) => db === "didactic")
      );
      for (const presTable of presTables) {
        genericTablePopulate(presTable as CCInteractiveTable<any>, cc);
      }
      cc.update(cc.struct.structure);
    }
  );
  cc.addButton(
    "primary",
    `<span>Sync section with CV</span>`,
    {
      relative: "after",
      selector: `[data-key="committees_header_sync_binding"]`
    },
    "syncSection",
    () => {
      if (!cc.checkPermissions().writableAsMenteeIdentity()) {
        return;
      }
      const allTables = cc.allCustomSectionData() as CCInteractiveTable[];
      const committeeTables = allTables.filter((t) =>
        t.baseClass.startsWith("committee")
      );
      for (const committeeTable of committeeTables) {
        genericTablePopulate(committeeTable as CCInteractiveTable<any>, cc);
      }
      cc.update(cc.struct.structure);
    }
  );

  const positions = getLastPosition(cc.newDef, { returnAll: true }) ?? [];
  const lastPosition = positions[positions.length - 1] ?? {};
  lastPosition.title ??= "";
  cc.setValueIfPredicate(
    "faculty_rank",
    lastPosition.title,
    (currentValue: string) => {
      if (!Boolean(currentValue)) return true;
      if (positions.some((position: any) => position.title === currentValue)) {
        if (currentValue != lastPosition.title) {
          return true;
        }
      }
      return false;
    }
  );
  const stanfordLineMap = cc.contextConstants["stanfordLineMap"] ?? {};
  const stanfordLine = stanfordLineMap[cc.newDef.narrative?.stanfordLine] ?? "";
  cc.setValueIfPredicate(
    "faculty_line",
    stanfordLine,
    (currentValue: string) => {
      if (!Boolean(currentValue)) return true;
      if (Object.values(stanfordLineMap).includes(currentValue)) {
        if (currentValue != stanfordLine) {
          return true;
        }
      }
      return false;
    }
  );

  cc.addSectionJumps({
    faculty_rank: { tab: "facultyAppointments" },
    faculty_line: { tab: "overview" },
    courses_jump_binding: {
      tab: "teaching",
      subtab: "Teaching of Students in Courses"
    },
    grants_jump_binding: { tab: "projects" },
    peer_original_jump_binding: {
      tab: "scholarship",
      subtab: "Research Investigations"
    },
    peer_other_jump_binding: { tab: "scholarship", subtab: "otherPeer" },
    peer_articles_jump_binding: {
      tab: "scholarship",
      subtab: "reviewArticle"
    },
    abstracts_jump_binding: { tab: "scholarship", subtab: "abstracts" },
    chapters_jump_binding: { tab: "scholarship", subtab: "chapters" },
    books_jump_binding: { tab: "scholarship", subtab: "books" },
    book_reviews_jump_binding: { tab: "scholarship", subtab: "reviews" },
    digital_pub_jump_binding: { tab: "scholarship", subtab: "nonPrint" },
    pres_local_jump_binding: { tab: "presentations", subtab: "local" },
    pres_regional_jump_binding: { tab: "presentations", subtab: "regional" },
    pres_national_jump_binding: { tab: "presentations", subtab: "national" },
    pres_international_jump_binding: {
      tab: "presentations",
      subtab: "international"
    },
    committee_not_jump_binding: { tab: "committeeService", subtab: "public" },
    committee_local_jump_binding: {
      tab: "committeeService",
      subtab: "public"
    },
    committee_regional_jump_binding: {
      tab: "committeeService",
      subtab: "public"
    },
    committee_national_jump_binding: {
      tab: "committeeService",
      subtab: "public"
    },
    committee_international_jump_binding: {
      tab: "committeeService",
      subtab: "public"
    },
    committee_uni_jump_binding: {
      tab: "committeeService",
      subtab: "institutional"
    }
  });
  cc.setCollapseRanges(
    {
      from: `[data-collapse-mark="0"]`,
      to: `[data-collapse-mark="I"]`
    },
    {
      from: `[data-collapse-mark="I"]`,
      to: `[data-collapse-mark="II"]`
    },
    {
      from: `[data-collapse-mark="II"]`,
      to: `[data-collapse-mark="III"]`
    },
    {
      from: `[data-collapse-mark="III"]`,
      to: `[data-collapse-mark="IV"]`
    },
    {
      from: `[data-collapse-mark="IV"]`,
      to: `[data-collapse-mark="V"]`
    },
    {
      from: `[data-collapse-mark="V"]`,
      to: `[data-collapse-mark="VI"]`
    },
    {
      from: `[data-collapse-mark="VI"]`,
      to: `[data-collapse-mark="VII"]`
    },
    {
      from: `[data-collapse-mark="VII"]`,
      to: `.printableContents:last-child`
    }
  );

  if (cc.checkPermissions().statusChangeableAsMenteeIdentity()) {
    cc.appendStatusButtons(false, DISABLE_EMAILS);
  }
  cc.update(cc.struct.structure);
  cc.contextMethods.updateLayout();
}
